/**
 * ProjectionInputCells - Reusable input cell components for projections
 *
 * Extracted from ProjectionInputView for maintainability.
 * These components handle the rendering of editable/read-only projection input cells.
 */

import React from 'react';

interface ProjectionInputCellProps {
  department: string;
  metric: string;
  value: number | null;
  isEditing: boolean;
  isDirty: boolean;
  isPercentage?: boolean;
  colorClass?: string;
  formatCurrency: (value: number) => string;
  onValueChange: (department: string, metric: string, value: number, inputType?: 'amount' | 'percentage') => void;
}

/**
 * Renders an input cell for projection values (amount or percentage)
 */
export const ProjectionInputCell: React.FC<ProjectionInputCellProps> = ({
  department,
  metric,
  value,
  isEditing,
  isDirty,
  isPercentage = false,
  colorClass = '',
  formatCurrency,
  onValueChange,
}) => {
  if (!isEditing) {
    // Read-only view
    return (
      <td className={`px-2 py-2 text-right text-sm font-semibold ${colorClass || 'bg-blue-50'}`}>
        {value !== null ? (
          isPercentage ? `${value.toFixed(1)}%` : formatCurrency(value)
        ) : '--'}
      </td>
    );
  }

  // Editable input
  return (
    <td className={`px-1 py-1 ${colorClass || 'bg-blue-50'}`}>
      <div className="flex items-center">
        <input
          type="number"
          step={isPercentage ? '0.1' : '1'}
          value={value ?? ''}
          onChange={(e) => {
            const newValue = parseFloat(e.target.value) || 0;
            onValueChange(department, metric, newValue, isPercentage ? 'percentage' : 'amount');
          }}
          className={`w-full px-2 py-1 text-right text-sm border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            isDirty ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
          }`}
          placeholder={isPercentage ? '0.0' : '0'}
        />
        {isPercentage && <span className="ml-1 text-xs text-gray-500">%</span>}
      </div>
    </td>
  );
};

interface PerUnitInputCellProps {
  department: string;
  metric: 'GROSS' | 'FI_INCOME';
  perUnitValue: number | null;
  totalUnits: number;
  isEditing: boolean;
  isDirty: boolean;
  colorClass: string;
  formatCurrency: (value: number) => string;
  onValueChange: (department: string, metric: string, value: number, inputType?: 'amount' | 'percentage') => void;
}

/**
 * Renders a per-unit input cell for Gross/F&I projections
 * Shows calculated total and allows per-unit value input
 */
export const PerUnitInputCell: React.FC<PerUnitInputCellProps> = ({
  department,
  metric,
  perUnitValue,
  totalUnits,
  isEditing,
  isDirty,
  colorClass,
  formatCurrency,
  onValueChange,
}) => {
  const perUnitMetric = `${metric}_PER_UNIT`;
  const calculatedTotal = (perUnitValue || 0) * totalUnits;

  if (!isEditing) {
    // Read-only view - show calculated total in bold, per-unit value smaller
    return (
      <td className={`px-2 py-2 text-right text-sm ${colorClass}`}>
        {perUnitValue !== null && totalUnits > 0 ? (
          <div className="whitespace-nowrap">
            <span className="font-bold">{formatCurrency(calculatedTotal)}</span>
            <span className="text-xs text-gray-500 font-normal ml-1">{formatCurrency(perUnitValue)}/u</span>
          </div>
        ) : '--'}
      </td>
    );
  }

  // Editable input - enter per-unit value, show calculated total
  return (
    <td className={`px-1 py-1 ${colorClass}`}>
      <div className="flex flex-col">
        {/* Calculated total (read-only) */}
        <div className="text-right text-sm font-bold text-green-700 mb-1">
          {totalUnits > 0 ? formatCurrency(calculatedTotal) : '--'}
        </div>
        {/* Per-unit input */}
        <div className="flex items-center">
          <span className="text-xs text-gray-500 mr-1">$/u:</span>
          <input
            type="number"
            step="1"
            value={perUnitValue ?? ''}
            onChange={(e) => {
              const newValue = parseFloat(e.target.value) || 0;
              onValueChange(department, perUnitMetric, newValue, 'amount');
            }}
            className={`w-full px-2 py-1 text-right text-sm border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              isDirty ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
            }`}
            placeholder="0"
          />
        </div>
      </div>
    </td>
  );
};

interface AccountExpenseInputCellProps {
  department: string;
  accountCode: string;
  percentValue: number | null;
  projectedGross: number;
  isEditing: boolean;
  isDirty: boolean;
  formatCurrency: (value: number) => string;
  onValueChange: (department: string, metric: string, value: number, inputType?: 'amount' | 'percentage') => void;
}

/**
 * Renders an account-level expense input cell
 * Shows calculated amount and allows percentage input
 */
export const AccountExpenseInputCell: React.FC<AccountExpenseInputCellProps> = ({
  department,
  accountCode,
  percentValue,
  projectedGross,
  isEditing,
  isDirty,
  formatCurrency,
  onValueChange,
}) => {
  const metric = `EXPENSE_${accountCode}`;
  const calculatedAmount = percentValue !== null ? Math.round((percentValue / 100) * projectedGross) : 0;

  if (!isEditing) {
    // Read-only view - show calculated amount and percentage
    return (
      <td className="min-w-[120px] px-2 py-1.5 text-right text-xs bg-gray-50">
        {percentValue !== null ? (
          <div className="whitespace-nowrap">
            <span className="font-semibold">{formatCurrency(calculatedAmount)}</span>
            <span className="text-gray-500 ml-1">{percentValue.toFixed(1)}%</span>
          </div>
        ) : '--'}
      </td>
    );
  }

  // Editable input - enter percentage, show calculated amount
  return (
    <td className="min-w-[120px] px-1 py-1 bg-gray-50">
      <div className="flex flex-col">
        {/* Calculated amount (read-only) */}
        <div className="text-right text-xs font-semibold text-orange-700 mb-0.5">
          {projectedGross > 0 && percentValue !== null ? formatCurrency(calculatedAmount) : '--'}
        </div>
        {/* Percentage input */}
        <div className="flex items-center justify-end">
          <input
            type="number"
            step="0.1"
            value={percentValue ?? ''}
            onChange={(e) => {
              const newValue = parseFloat(e.target.value) || 0;
              onValueChange(department, metric, newValue, 'percentage');
            }}
            className={`w-16 px-1 py-0.5 text-right text-xs border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              isDirty ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
            }`}
            placeholder="0.0"
          />
          <span className="ml-0.5 text-xs text-gray-500">%</span>
        </div>
      </div>
    </td>
  );
};

interface BrandUnitInputCellProps {
  department: string;
  brand: string;
  value: number | null;
  isEditing: boolean;
  isDirty: boolean;
  colorClass: string;
  onValueChange: (department: string, metric: string, value: number, inputType?: 'amount' | 'percentage') => void;
}

/**
 * Renders a brand-level unit input cell
 */
export const BrandUnitInputCell: React.FC<BrandUnitInputCellProps> = ({
  department,
  brand,
  value,
  isEditing,
  isDirty,
  colorClass,
  onValueChange,
}) => {
  const metric = `UNITS_${brand}`;

  if (!isEditing) {
    return (
      <td className={`min-w-[120px] px-2 py-1.5 text-right text-xs ${colorClass}`}>
        {value !== null && value > 0 ? value.toLocaleString() : '--'}
      </td>
    );
  }

  return (
    <td className={`min-w-[120px] px-1 py-1 ${colorClass}`}>
      <input
        type="number"
        step="1"
        value={value ?? ''}
        onChange={(e) => {
          const newValue = parseInt(e.target.value) || 0;
          onValueChange(department, metric, newValue, 'amount');
        }}
        className={`w-full px-2 py-1 text-right text-xs border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
          isDirty ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
        }`}
        placeholder="0"
      />
    </td>
  );
};
